import { TrackingCategory } from '@tn/foundation/entities/tracking/const';
import { TrackerEngine } from '@tn/foundation/entities/tracking/lib/tracker-engine';
import { PAGE_TYPES, TRACK_LINK_NAMES } from '@tn/foundation/utils/constants/tracking';

/** i18n / cookie key for the “password changed” success banner after change-password redirect. */
export const PASSWORD_RESET_SUCCESS_BANNER_MESSAGE = 'password_has_been_successfully_changed';

/**
 * Tracks password reset success for reporting.
 * Invoked from `navigateToAccountPage` after the banner cookie is set and before the full-page redirect
 * to `/account`, so tracking runs in the flow before navigation while the banner still comes from SSR props.
 *
 * @param message - The banner message translation key (must match {@link PASSWORD_RESET_SUCCESS_BANNER_MESSAGE} to emit)
 */
export const trackPasswordResetSuccess = (message: string): void => {
    if (message === PASSWORD_RESET_SUCCESS_BANNER_MESSAGE) {
        try {
            void TrackerEngine.update({
                type: TRACK_LINK_NAMES.TRACK_RESET_PASSWORD_SUCCESS,
                detail: {
                    context: {
                        pageType: PAGE_TYPES.ACCOUNT,
                    },
                },
                category: TrackingCategory.App.DISPLAY,
            });
        } catch (e) {
            // Don't allow tracking failures to break user flow
        }
    }
};

/** i18n key set in account flow when edit-email request succeeds (info banner). */
export const EDIT_PASSWORD_EMAIL_LINK_SENT_MESSAGE =
    'a_password_reset_email_has_been_sent_to_your_email_address';

/**
 * Fires when the account page shows the “we’ve sent you a link…” banner after edit password email sent.
 */
export const trackEditPasswordLinkSentBannerDisplay = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_EDIT_PASSWORD_LINK_SENT_VIEW,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.DISPLAY,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/** Output message returned by `optInActor` when the user opts in to marketing communications. */
export const MARKETING_COMMUNICATIONS_OPT_IN_MESSAGE = 'marketing_communications_opt_in';

/** Output message returned by `optInActor` when the user opts out of marketing communications. */
export const MARKETING_COMMUNICATIONS_OPT_OUT_MESSAGE = 'marketing_communications_opt_out';

/**
 * Fires when the user clicks the "Read FAQs" link on the Account page.
 */
export const trackReadFaqClick = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_FAQ,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the user clicks the "Sign Out" link on the Account page.
 */
export const trackSignOutClick = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_SIGN_OUT,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the user clicks the "Change Email" link on the Account page.
 */
export const trackEditEmailClick = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_EDIT_EMAIL,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/** i18n key returned by `editEmailActor` when the change-email link is sent (info banner). */
export const EDIT_EMAIL_LINK_SENT_MESSAGE = 'weve_sent_you_a_link_to_reset_your_email';

/**
 * Fires when the account page shows the “we’ve sent you a link…” banner after the
 * change-email link is sent.
 */
export const trackEditEmailLinkSentBannerDisplay = (): void => {
    try {
        TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_EDIT_EMAIL_LINK_SENT_VIEW,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.DISPLAY,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the user clicks the “Confirm Email” CTA on the change-email screen.
 * The pageType must match the change-email page per tracking spec.
 */
export const trackConfirmEmailEditSelect = (pageType?: string): void => {
    try {
        TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_CONFIRM_EMAIL_EDIT_SELECT,
            detail: {
                context: {
                    pageType: pageType ?? PAGE_TYPES.CHANGE_EMAIL,
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/** i18n / cookie key for the “email changed” success banner after the change-email redirect. */
export const EMAIL_CHANGE_SUCCESS_BANNER_MESSAGE =
    'you_have_successfully_changed_your_email_address';

/**
 * Tracks email change success for reporting.
 * Invoked from `navigateToAccountPage` after the banner cookie is set and before the full-page redirect
 * to `/account`, so tracking runs in the flow before navigation while Adobe/Tealium is still attached.
 *
 * @param message - The banner message translation key (must match {@link EMAIL_CHANGE_SUCCESS_BANNER_MESSAGE} to emit)
 */
export const trackEmailChangeSuccess = (message: string): void => {
    if (message !== EMAIL_CHANGE_SUCCESS_BANNER_MESSAGE) {
        return;
    }
    try {
        TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_EDIT_EMAIL_SUCCESS,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.DISPLAY,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the user toggles the marketing preferences checkbox on the Account page.
 *
 * @param isChecked - The new checked state of the checkbox (true = select, false = deselect).
 */
export const trackMarketingCheckboxToggle = (isChecked: boolean): void => {
    try {
        void TrackerEngine.update({
            type: isChecked
                ? TRACK_LINK_NAMES.TRACK_MARKETING_CHECKBOX_SELECT
                : TRACK_LINK_NAMES.TRACK_MARKETING_CHECKBOX_DESELECT,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.INTERACT,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the marketing communications subscribe success banner becomes visible
 * after the user selects the marketing checkbox.
 */
export const trackMarketingSubscribeBannerDisplay = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_MARKETING_COMS_SUBSCRIBE_VIEW,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.DISPLAY,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};

/**
 * Fires when the marketing communications unsubscribe success banner becomes visible
 * after the user deselects the marketing checkbox.
 */
export const trackMarketingUnsubscribeBannerDisplay = (): void => {
    try {
        void TrackerEngine.update({
            type: TRACK_LINK_NAMES.TRACK_MARKETING_COMS_UNSUBSCRIBE_VIEW,
            detail: {
                context: {
                    pageType: PAGE_TYPES.ACCOUNT,
                },
            },
            category: TrackingCategory.App.DISPLAY,
        });
    } catch {
        // Don't allow tracking failures to break user flow
    }
};
